import { Fragment, useEffect, useMemo } from 'react';
import { Marker, Polyline } from 'react-leaflet';
import { useQuery } from '@tanstack/react-query';
import type { Location } from '../../store/realtimeStore';
import { usePlatoonStore } from '../../store/platoonStore';
import { bearingDeg, geometryCentroid, normalizeGeometry } from '../../lib/geo';
import type { Farmbase, LatLon, Target } from '../../types/domain';
import { routeApi } from '../../services/api';
import { getPlatoon } from './mapUtils';
import { arrowheadIcon, targetRenderClass, OPS_COLORS } from './opsChips';

// Routes are stable — cache each one aggressively so we don't re-fetch on ticks.
const ROUTE_STALE_MS = 30 * 60 * 1000;

// Round to ~11 m so GPS jitter reuses the same cached route key.
function round5(n: number): number {
  return Math.round(n * 1e4) / 1e4;
}

interface Segment {
  key: string;
  from: LatLon;
  to: LatLon;
  color: string;
  opacity: number;
  executing: boolean;
  scheduled: boolean;
}

// One intent line: a ROAD route platoon→target (React Query, keyed by rounded
// endpoints, long staleTime). Falls back to a straight 2-point line while the
// query loads, on any error/404, or when the backend flags `fallback:true`, so
// something always shows. Never blocks render on the fetch.
function IntentLine({ from, to, color, opacity, executing, scheduled }: Omit<Segment, 'key'>) {
  const fromLat = round5(from.lat);
  const fromLon = round5(from.lon);
  const toLat = round5(to.lat);
  const toLon = round5(to.lon);

  const { data, isError } = useQuery({
    queryKey: ['route', fromLat, fromLon, toLat, toLon],
    queryFn: async () => (await routeApi.getRoute(from.lat, from.lon, to.lat, to.lon)).data,
    staleTime: ROUTE_STALE_MS,
    gcTime: ROUTE_STALE_MS,
    retry: false, // don't hammer /route (it 404s until the backend deploys)
    refetchOnWindowFocus: false,
  });

  const routed =
    !isError && data && !data.fallback && Array.isArray(data.geometry) && data.geometry.length >= 2
      ? data.geometry.map((p) => [p.lat, p.lon] as [number, number])
      : null;

  // Straight 2-point fallback keeps the intent visible when there is no route.
  const line: [number, number][] = routed ?? [
    [from.lat, from.lon],
    [to.lat, to.lon],
  ];

  // Arrowhead at the TARGET end, aligned to the final segment's bearing.
  const n = line.length;
  const tip = line[n - 1];
  const prev = line[n - 2] ?? line[0];
  const bearing = bearingDeg({ lat: prev[0], lon: prev[1] }, { lat: tip[0], lon: tip[1] });

  return (
    <>
      <Polyline
        positions={line}
        pathOptions={{
          color,
          weight: executing ? 3 : scheduled ? 2 : 2.5,
          opacity,
          dashArray: executing ? undefined : '8 8',
        }}
      />
      <Marker
        position={tip}
        icon={arrowheadIcon(bearing, color, scheduled ? 0.45 : 1)}
        interactive={false}
        zIndexOffset={800}
      />
    </>
  );
}

// F-4: routed intent line from each assigned platoon's current/last position to
// its target's representative point. Green dashed while ASSIGNED, orange solid
// while EXECUTING, with an arrowhead at the target end. A platoon with NO live
// GPS anchors its arrow at its home farmbase (planned intent must still show).
export default function IntentArrows({
  targets,
  locations,
  farmbases,
}: {
  targets: Target[];
  locations: Location[]; // already unit-scoped + display-filtered (same list as the markers)
  farmbases: Farmbase[]; // fallback anchors for platoons that are not tracking
}) {
  // Platoon records give the platoonId -> farmbaseId binding for the fallback.
  const { platoons, fetchPlatoons } = usePlatoonStore();
  useEffect(() => {
    if (platoons.length === 0) fetchPlatoons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPlatoons]);

  // Latest known LIVE position per platoon.
  const platoonPos = useMemo(() => {
    const best = new Map<string, Location>();
    locations.forEach((loc) => {
      const pid = loc.platoonId || getPlatoon(loc)?.id;
      if (!pid) return;
      const prev = best.get(pid);
      if (!prev || new Date(loc.serverTime) > new Date(prev.serverTime)) best.set(pid, loc);
    });
    const pos = new Map<string, LatLon>();
    best.forEach((loc, pid) => pos.set(pid, { lat: loc.latitude, lon: loc.longitude }));
    return pos;
  }, [locations]);

  // Farmbase representative points (same rep rule as FarmbaseLayer).
  const farmbaseRep = useMemo(() => {
    const m = new Map<string, LatLon>();
    farmbases.forEach((fb) => {
      const rep =
        fb.repLat != null && fb.repLng != null
          ? { lat: fb.repLat, lon: fb.repLng }
          : geometryCentroid(normalizeGeometry(fb) ?? { shape: 'POINT' });
      if (rep) m.set(fb.id, rep);
    });
    return m;
  }, [farmbases]);

  // Flatten the drawn intents into stable-keyed segments; each renders its own
  // routed <IntentLine> (one React Query per segment — hooks stay stable).
  const segments = useMemo<Segment[]>(() => {
    // Live position first; otherwise the platoon's home farmbase.
    const startFor = (platoonId: string): LatLon | undefined => {
      const live = platoonPos.get(platoonId);
      if (live) return live;
      const fbId = platoons.find((p) => p.id === platoonId)?.farmbaseId;
      return fbId ? farmbaseRep.get(fbId) : undefined;
    };

    const segs: Segment[] = [];
    targets
      // ASSIGNED/EXECUTING as before; SCHEDULED (window not yet open) also shows
      // its planned intent when the commander already assigned platoons.
      .filter(
        (t) =>
          t.status === 'ASSIGNED' ||
          t.status === 'EXECUTING' ||
          (targetRenderClass(t) === 'scheduled' && (t.assignments ?? []).length > 0)
      )
      .forEach((t) => {
        const rep =
          t.repLat != null && t.repLng != null ? { lat: t.repLat, lon: t.repLng } : geometryCentroid(t);
        if (!rep) return;
        const executing = t.status === 'EXECUTING';
        const scheduled = !executing && t.status !== 'ASSIGNED';
        const color = executing ? OPS_COLORS.intentExecuting : OPS_COLORS.intentAssigned;
        // Planned intent reads quieter than live intent.
        const opacity = scheduled ? 0.45 : 0.9;

        (t.assignments ?? []).forEach((a) => {
          const from = startFor(a.platoonId);
          if (!from) return;
          segs.push({
            key: `intent-${t.id}-${a.platoonId}`,
            from,
            to: rep,
            color,
            opacity,
            executing,
            scheduled,
          });
        });
      });
    return segs;
  }, [targets, platoonPos, farmbaseRep, platoons]);

  return (
    <>
      {segments.map((s) => (
        <Fragment key={s.key}>
          <IntentLine
            from={s.from}
            to={s.to}
            color={s.color}
            opacity={s.opacity}
            executing={s.executing}
            scheduled={s.scheduled}
          />
        </Fragment>
      ))}
    </>
  );
}
